// src/pages/org/OrgInsightsPage.js
import React, { useEffect, useMemo, useState } from "react";
import JSZip from "jszip";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { buildInsightsCsv, computeOrgMetrics, computeOrgMetricsFromFirestore, fetchOrgSessionsFiltered, computeOrgMetricsFromSessions, fetchOrgSettings, fetchOrgSeatSnapshot, normalizeOrgSession } from "../../utils/orgData";
import Card from "../../ui/Card";
import Stat from "../../ui/Stat";
import Table from "../../ui/Table";
import Progress from "../../ui/Progress";
import PilotOperationsSection from "../../components/PilotOperationsSection";
import { useAuth } from "../../context/AuthContext";
import { fetchCheckinWindow } from "../../utils/checkins";
import { getSessionDisplayEmail, getSessionDisplayName, getSessionDisplayText } from "../../utils/sessionPrivacy";

const chartTooltipStyle = {
  background: "var(--ui-surface)",
  border: "1px solid var(--ui-border)",
  color: "var(--ui-text)",
};

const pieColors = [
  "var(--ui-accent)",
  "var(--ui-accent-2)",
  "var(--ui-accent-3)",
  "var(--ui-accent-4)",
  "var(--ui-accent-5)",
];

const fallbackTopBlocks = [
  { name: "Focus Drift", count: 14 },
  { name: "Low Energy", count: 9 },
  { name: "Time Warp", count: 6 },
  { name: "Anxious Loop", count: 4 },
];

const fallbackGameShare = [
  { name: "Move-It", value: 42 },
  { name: "MaxIt", value: 28 },
  { name: "DriftStop", value: 18 },
  { name: "Reset", value: 12 },
];

const fallbackSessions = [
  {
    user: "Samira L.",
    dateISO: "2024-08-06T10:24:00Z",
    msgs: 18,
    preview: "Needed a reset before a deadline. Found calm in 6 minutes.",
  },
  {
    user: "Kai B.",
    dateISO: "2024-08-05T14:10:00Z",
    msgs: 11,
    preview: "Work stress spiked after a meeting; used Move-It game.",
  },
  {
    user: "Jules M.",
    dateISO: "2024-08-04T19:02:00Z",
    msgs: 22,
    preview: "Focus drifted during study, ran a brief grounding sequence.",
  },
  {
    user: "Priya K.",
    dateISO: "2024-08-03T08:41:00Z",
    msgs: 9,
    preview: "Morning check-in for anxiety. Breath pacing helped.",
  },
];

function formatDate(dateISO) {
  if (!dateISO) return "-";
  if (typeof dateISO?.toDate === "function") {
    const date = dateISO.toDate();
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
  }
  const date = new Date(dateISO);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
}

export default function OrgInsightsPage() {
  const { user, activeOrgId } = useAuth();
  const [metrics, setMetrics] = useState(() => computeOrgMetrics({}) || {});
  const [sessionRows, setSessionRows] = useState([]);
  const [orgSettings, setOrgSettings] = useState({});
  const [seatSnapshot, setSeatSnapshot] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [gameFilter, setGameFilter] = useState("all");
  const [rangeFilter, setRangeFilter] = useState("30");
  const [blockFilter, setBlockFilter] = useState("all");
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [includeRaw, setIncludeRaw] = useState(false);
  const [csvMode, setCsvMode] = useState("compact");
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [cohortDays, setCohortDays] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [sessionsError, setSessionsError] = useState("");
  const orgId = activeOrgId || user?.orgId || "org_default";
  const orgType = (orgSettings?.type || "").toLowerCase();
  const isLiveOrg = orgId === "org_shuttle" || orgType === "pilot" || orgType === "live";
  const allowFallback = !isLiveOrg;

  useEffect(() => {
    if (!user?.uid) return;
    fetchOrgSettings(user, orgId)
      .then((settings) => setOrgSettings(settings || {}))
      .catch(() => {});
  }, [user, orgId]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchOrgSeatSnapshot(user, orgId, 30)
      .then((snapshot) => setSeatSnapshot(snapshot || null))
      .catch(() => setSeatSnapshot(null));
  }, [user, orgId]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchCheckinWindow(orgId, 7)
      .then((rows) => setCheckins(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [user, orgId]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    setSessionsLoaded(false);
    setSessionsError("");
    fetchOrgSessionsFiltered(user, orgId, { role: roleFilter, game: gameFilter, days: rangeFilter })
      .then((sessions) => {
        if (!mounted) return;
        const normalized = Array.isArray(sessions) ? sessions.map(normalizeOrgSession) : [];
        setSessionRows(normalized);
        setSessionsLoaded(true);
        if (normalized.length) {
          setMetrics(computeOrgMetricsFromSessions(normalized, { allowFallback }));
        } else {
          computeOrgMetricsFromFirestore(user, orgId, { allowFallback })
            .then((next) => { if (mounted && next) setMetrics(next); })
            .catch(() => {
              if (mounted) setSessionsError("Unable to load organisation session metrics.");
            });
        }
      })
      .catch(() => {
        if (!mounted) return;
        setSessionRows([]);
        setSessionsLoaded(true);
        setSessionsError("Unable to load organisation session data.");
      });
    return () => {
      mounted = false;
    };
  }, [user, orgId, roleFilter, gameFilter, rangeFilter, allowFallback]);
  const kpis = metrics.kpis || {};
  const seats = metrics.seats || { used: kpis.seatsUsed || 0, total: 0 };
  const rawSessions = useMemo(() => {
    if (sessionRows.length) return sessionRows;
    if (Array.isArray(metrics.recentSessions) && metrics.recentSessions.length > 0) return metrics.recentSessions;
    return allowFallback ? fallbackSessions : [];
  }, [sessionRows, metrics.recentSessions, allowFallback]);

  const filteredSessions = useMemo(() => {
    if (blockFilter === "all") return rawSessions;
    return rawSessions.filter((s) => s.block === blockFilter);
  }, [rawSessions, blockFilter]);

  const topBlocks = Array.isArray(metrics.topBlocks) && metrics.topBlocks.length > 0
    ? metrics.topBlocks
    : (allowFallback ? fallbackTopBlocks : []);
  const gameShare = Array.isArray(metrics.gameShare) && metrics.gameShare.length > 0
    ? metrics.gameShare
    : (allowFallback ? fallbackGameShare : []);
  const recentSessions = filteredSessions.length ? filteredSessions : rawSessions;
  const hasSessionData = recentSessions.length > 0;
  const recentSessionsTitle = rangeFilter === "0"
    ? "Recent Sessions (all time)"
    : `Recent Sessions (${rangeFilter} days)`;

  const seatsTotal = Number.isFinite(Number(seatSnapshot?.seatsTotal))
    ? Number(seatSnapshot.seatsTotal)
    : (Number.isFinite(Number(seats.total)) ? Number(seats.total) : 0);
  const seatsUsed = Number.isFinite(Number(seatSnapshot?.seatsUsed))
    ? Number(seatSnapshot.seatsUsed)
    : (Number.isFinite(Number(seats.used)) ? Number(seats.used) : 0);
  const seatsProgress = seatsTotal > 0 ? Math.round((seatsUsed / seatsTotal) * 100) : 0;

  const avgMsgs = Number.isFinite(Number(kpis.avgMsgsPerSession))
    ? Number(kpis.avgMsgsPerSession).toFixed(1)
    : "0.0";

  const checkinCount = checkins.length;
  const avgMood = checkinCount
    ? (checkins.reduce((sum, c) => sum + Number(c.mood || 0), 0) / checkinCount).toFixed(1)
    : "0.0";
  const checkinTags = (() => {
    const counts = new Map();
    checkins.forEach((c) => (c.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([tag]) => tag)
      .join(", ") || "—";
  })();
  const viewerRole = user?.role || "member";
  const maskSessionText = (session, text) => getSessionDisplayText(text, session, viewerRole);
  const displaySessionName = (session, fallback = "Member") => getSessionDisplayName(session, viewerRole, fallback);

  const tableHeaders = ["User", "Date", "Messages", "Preview"];
  const tableRows = recentSessions.map((session) => [
    displaySessionName(session, "User"),
    formatDate(session.dateISO),
    session.msgs,
    maskSessionText(session, session.preview),
  ]);

  const uniqueGames = Array.from(new Set(rawSessions.map((s) => s.game).filter(Boolean)));
  const uniqueBlocks = Array.from(new Set(rawSessions.map((s) => s.block).filter(Boolean)));

  const topUsers = useMemo(() => {
    const counts = new Map();
    filteredSessions.forEach((s) => {
      const key = s.user || "Unknown";
      const entry = counts.get(key) || { user: key, sessions: 0, messages: 0 };
      entry.sessions += 1;
      entry.messages += Number(s.msgs || 0);
      counts.set(key, entry);
    });
    return Array.from(counts.values())
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 8);
  }, [filteredSessions]);

  const topUserRows = topUsers.map((u) => [maskSessionText({ user: u.user }, u.user), u.sessions, u.messages]);

  const userSessions = useMemo(() => {
    if (!selectedUser) return [];
    return filteredSessions.filter((s) => (s.user || "Unknown") === selectedUser);
  }, [filteredSessions, selectedUser]);

  const userTopBlocks = useMemo(() => {
    if (!userSessions.length) return [];
    const counts = new Map();
    userSessions.forEach((s) => {
      if (!s.block) return;
      counts.set(s.block, (counts.get(s.block) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [userSessions]);

  const userTopGames = useMemo(() => {
    if (!userSessions.length) return [];
    const counts = new Map();
    userSessions.forEach((s) => {
      if (!s.game) return;
      counts.set(s.game, (counts.get(s.game) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [userSessions]);

  const trendData = useMemo(() => {
    const days = Number(rangeFilter) > 0 ? Number(rangeFilter) : 30;
    const map = new Map();
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(Date.now() - i * 24 * 3600 * 1000);
      const key = d.toISOString().slice(0, 10);
      map.set(key, { date: key, sessions: 0 });
    }
    rawSessions.forEach((s) => {
      const key = (s.dateISO || s.createdAt || "").toString().slice(0, 10);
      if (map.has(key)) {
        map.get(key).sessions += 1;
      }
    });
    return Array.from(map.values());
  }, [rawSessions, rangeFilter]);

  const ratingSessions = useMemo(
    () => rawSessions.filter((s) => s.rating && Number(s.rating.score) > 0),
    [rawSessions]
  );

  const avgRating = useMemo(() => {
    if (!ratingSessions.length) return 0;
    const total = ratingSessions.reduce((sum, s) => sum + Number(s.rating.score || 0), 0);
    return (total / ratingSessions.length).toFixed(2);
  }, [ratingSessions]);

  const recentFeedback = useMemo(() => {
    return ratingSessions
      .filter((s) => s.rating?.feedback)
      .sort((a, b) => (b.rating?.createdAt || 0) - (a.rating?.createdAt || 0))
      .slice(0, 4);
  }, [ratingSessions]);

  const exportRatingsCsv = () => {
    const { safeName, stamp } = getExportMeta();
    const header = ["User", "Score", "Feedback", "Date"];
    const rows = ratingSessions.map((s) => [
      displaySessionName(s, "User"),
      s.rating?.score || "",
      maskSessionText(s, s.rating?.feedback || ""),
      s.rating?.createdAt ? new Date(s.rating.createdAt).toISOString() : "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    downloadCsv(csv, `nia-ratings-${safeName}-${stamp}.csv`);
  };

  const cohortSummary = useMemo(() => {
    const now = Date.now();
    const windows = [
      { label: "Last 7 days", days: 7 },
      { label: "Last 30 days", days: 30 },
      { label: "Last 90 days", days: 90 },
    ];
    return windows.map((window) => {
      const cutoff = now - window.days * 24 * 3600 * 1000;
      const sessionsInWindow = rawSessions.filter((s) => {
        const ts = new Date(s.dateISO || s.createdAt || 0).getTime();
        return ts >= cutoff;
      });
      const users = new Set(sessionsInWindow.map((s) => s.user).filter(Boolean));
      return {
        ...window,
        sessions: sessionsInWindow.length,
        users: users.size,
      };
    });
  }, [rawSessions]);

  const cohortSessions = useMemo(() => {
    if (!cohortDays) return [];
    const cutoff = Date.now() - Number(cohortDays) * 24 * 3600 * 1000;
    return rawSessions.filter((s) => {
      const ts = new Date(s.dateISO || s.createdAt || 0).getTime();
      return ts >= cutoff;
    });
  }, [rawSessions, cohortDays]);

  const cohortUsers = useMemo(() => {
    if (!cohortSessions.length) return 0;
    return new Set(cohortSessions.map((s) => s.user).filter(Boolean)).size;
  }, [cohortSessions]);

  const cohortTopBlocks = useMemo(() => {
    if (!cohortSessions.length) return [];
    const counts = new Map();
    cohortSessions.forEach((s) => {
      if (!s.block) return;
      counts.set(s.block, (counts.get(s.block) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [cohortSessions]);

  const cohortTopGames = useMemo(() => {
    if (!cohortSessions.length) return [];
    const counts = new Map();
    cohortSessions.forEach((s) => {
      if (!s.game) return;
      counts.set(s.game, (counts.get(s.game) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [cohortSessions]);

  const roleGameHighlights = useMemo(() => {
    const roles = ["member", "coach", "admin", "superadmin"];
    return roles
      .map((role) => {
        const sessions = filteredSessions.filter((s) => (s.role || "member") === role);
        if (!sessions.length) return null;
        const counts = new Map();
        sessions.forEach((s) => {
          if (!s.game) return;
          counts.set(s.game, (counts.get(s.game) || 0) + 1);
        });
        if (!counts.size) return null;
        const [game, count] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
        return { role, game, count };
      })
      .filter(Boolean);
  }, [filteredSessions]);

  const exportTrendCsv = () => {
    const header = ["Date", "Sessions"];
    const rows = trendData.map((d) => [d.date, d.sessions]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "nia-insights-trend.csv";
    a.click();
  };

  const blocksByRole = useMemo(() => {
    const roles = ["member", "coach", "admin", "superadmin"];
    const blocks = Array.from(new Set(rawSessions.map((s) => s.block).filter(Boolean))).slice(0, 4);
    return roles.map((role) => {
      const row = { role };
      blocks.forEach((block) => {
        row[block] = rawSessions.filter((s) => (s.role || "member") === role && s.block === block).length;
      });
      return row;
    });
  }, [rawSessions]);

  const gamesByRole = useMemo(() => {
    const roles = ["member", "coach", "admin", "superadmin"];
    const games = Array.from(new Set(rawSessions.map((s) => s.game).filter(Boolean))).slice(0, 4);
    return roles.map((role) => {
      const row = { role };
      games.forEach((game) => {
        row[game] = rawSessions.filter((s) => (s.role || "member") === role && s.game === game).length;
      });
      return row;
    });
  }, [rawSessions]);

  const toFullCsv = (rows) => {
    const header = ["User", "Email", "Role", "Date", "Messages", "Preview", "Block", "Game"];
    const lines = rows.map((row) => [
      row.user || "",
      row.userEmail || "",
      row.role || "",
      row.dateISO || "",
      row.msgs || 0,
      row.preview || "",
      row.block || "",
      row.game || "",
    ]);
    return [header, ...lines].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
  };

  const getExportMeta = () => {
    const orgId = activeOrgId || user?.orgId || "org_default";
    let orgName = orgId;
    try {
      const stored = localStorage.getItem("org_profile");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.name) orgName = parsed.name;
      }
    } catch {}
    const safeName = String(orgName || orgId)
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "");
    const stamp = new Date().toISOString().slice(0, 10);
    return { orgId, orgName, safeName, stamp };
  };

  const downloadBlob = (blob, name) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  };

  const downloadCsv = (content, name) => {
    const blob = new Blob([content], { type: "text/csv" });
    downloadBlob(blob, name);
  };

  const exportInsightsSingle = () => {
    const { safeName, stamp } = getExportMeta();
    const csv = csvMode === "full" ? toFullCsv(recentSessions) : buildInsightsCsv(recentSessions);
    const suffix = csvMode === "full" ? "-full" : "";
    downloadCsv(csv, `nia-insights-${safeName}-${stamp}${suffix}.csv`);
  };

  const exportInsightsPack = async ({ includeRawSessions = false, mode = "compact" } = {}) => {
    const { safeName, stamp } = getExportMeta();
    const insightsCsv = mode === "full" ? toFullCsv(recentSessions) : buildInsightsCsv(recentSessions);
    const topUsersCsv = buildInsightsCsv(
      topUsers.map((u) => ({
        user: u.user,
        dateISO: "",
        msgs: u.messages,
        preview: "",
        block: "",
        game: "",
      }))
    );

    const gameShareCsv = (() => {
      const header = ["Game", "Value"];
      const rows = gameShare.map((g) => [g.name, g.value]);
      return [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    })();

    const trendCsv = (() => {
      const header = ["Date", "Sessions"];
      const rows = trendData.map((d) => [d.date, d.sessions]);
      return [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    })();

    const blocksByRoleCsv = (() => {
      const keys = Object.keys(blocksByRole[0] || {}).filter((k) => k !== "role");
      const header = ["Role", ...keys];
      const rows = blocksByRole.map((row) => [row.role, ...keys.map((k) => row[k] || 0)]);
      return [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    })();

    const gamesByRoleCsv = (() => {
      const keys = Object.keys(gamesByRole[0] || {}).filter((k) => k !== "role");
      const header = ["Role", ...keys];
      const rows = gamesByRole.map((row) => [row.role, ...keys.map((k) => row[k] || 0)]);
      return [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
    })();

    const suffix = mode === "full" ? "-full" : "";
    const zip = new JSZip();
    zip.file(`nia-insights-${safeName}-${stamp}${suffix}.csv`, insightsCsv);
    zip.file(`nia-top-users-${safeName}-${stamp}.csv`, topUsersCsv);
    zip.file(`nia-game-share-${safeName}-${stamp}.csv`, gameShareCsv);
    zip.file(`nia-trend-${safeName}-${stamp}.csv`, trendCsv);
    zip.file(`nia-blocks-by-role-${safeName}-${stamp}.csv`, blocksByRoleCsv);
    zip.file(`nia-games-by-role-${safeName}-${stamp}.csv`, gamesByRoleCsv);

    if (includeRawSessions) {
      const rawCsv = mode === "full" ? toFullCsv(rawSessions) : buildInsightsCsv(rawSessions);
      zip.file(`nia-raw-sessions-${safeName}-${stamp}${suffix}.csv`, rawCsv);
    }
    const blob = await zip.generateAsync({ type: "blob" });
    downloadBlob(blob, `nia-insights-pack-${safeName}-${stamp}.zip`);
  };

  return (
    <div className="org-insights">
      <div className="org-insights__header">
        <h2>Organisation Insights</h2>
        <p>Weekly engagement, focus blocks, and session snapshots.</p>
      </div>

      <PilotOperationsSection user={user} orgId={orgId} />

      <Card title="Filters">
        <div className="nia-form" style={{ display: "grid", gap: 12 }}>
          <div>
            <label>Role</label>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              <option value="member">Member</option>
              <option value="coach">Coach</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label>Game</label>
            <select value={gameFilter} onChange={(e) => setGameFilter(e.target.value)}>
              <option value="all">All games</option>
              {uniqueGames.map((game) => (
                <option key={game} value={game}>{game}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Block</label>
            <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <option value="all">All blocks</option>
              {uniqueBlocks.map((block) => (
                <option key={block} value={block}>{block}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Time range</label>
            <select value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="0">All time</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btn-ghost"
              onClick={exportInsightsSingle}
            >
              Export Insights CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowExportConfirm(true)}>
              Download Insights Pack (.zip)
            </button>
          </div>
        </div>
      </Card>

      {showExportConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "var(--color-overlay)", zIndex: 999 }}>
          <div style={{ maxWidth: 520, margin: "12% auto", background: "var(--ui-surface)", padding: 20, borderRadius: 12, border: "1px solid var(--ui-border)" }}>
            <h3 style={{ marginTop: 0 }}>Download Insights Pack (.zip)</h3>
            <p className="nia-muted">
              Choose format options before downloading. The ZIP will include {includeRaw ? "7" : "6"} CSV files.
            </p>
            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
              <label className="nia-inline">
                <input
                  type="checkbox"
                  checked={includeRaw}
                  onChange={(e) => setIncludeRaw(e.target.checked)}
                />
                <span>Include raw sessions export</span>
              </label>
              <label>
                <span className="nia-muted">CSV format</span>
                <select value={csvMode} onChange={(e) => setCsvMode(e.target.value)}>
                  <option value="compact">Compact</option>
                  <option value="full">Full (includes role + email)</option>
                </select>
              </label>
              <div className="nia-muted" style={{ fontSize: 13 }}>
                Compact keeps it lightweight; Full adds role + email columns.
              </div>
              <div className="nia-muted" style={{ fontSize: 13 }}>
                Files: insights, top users, game share, trend, blocks by role, games by role{includeRaw ? ", raw sessions" : ""}.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  await exportInsightsPack({ includeRawSessions: includeRaw, mode: csvMode });
                  setShowExportConfirm(false);
                }}
              >
                Download
              </button>
              <button className="btn btn-ghost" onClick={() => setShowExportConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="org-grid org-grid--kpis">
        <Card>
          <Stat label="Sessions this week" value={kpis.sessionsWeek ?? 0} subtle="Across all programs" />
        </Card>
        <Card>
          <Stat label="Active users" value={kpis.activeUsersWeek ?? 0} subtle="Seen in last 7 days" />
        </Card>
        <Card>
          <Stat label="Avg msgs / session" value={avgMsgs} subtle="Conversation depth" />
        </Card>
        <Card>
          <Stat label="Seats used" value={`${seatsUsed}/${seatsTotal || "-"}`} subtle="Current plan" />
          <Progress value={seatsProgress} />
        </Card>
        <Card>
          <Stat label="Check‑ins (7 days)" value={checkinCount} subtle={`Avg mood ${avgMood}`} />
          <div className="nia-muted" style={{ marginTop: 6 }}>Top themes: {checkinTags}</div>
        </Card>
      </div>

      {sessionsError ? (
        <Card>
          <div className="nia-muted">{sessionsError}</div>
        </Card>
      ) : null}

      <div className="org-grid org-grid--table">
        <Card title="Cohort Summary">
          {hasSessionData ? (
            <div className="nia-list">
              {cohortSummary.map((row) => (
                <button
                  key={row.label}
                  className="nia-list-btn"
                  onClick={() => {
                    setCohortDays(row.days);
                    setRangeFilter(String(row.days));
                  }}
                >
                  <span>{row.label}</span>
                  <span className="nia-pill">{row.sessions} sessions</span>
                  <span className="nia-pill">{row.users} users</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="nia-muted">
              {sessionsLoaded ? "No session data found for the current filters." : "Loading session data..."}
            </div>
          )}
          {cohortDays && (
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-ghost" onClick={() => setCohortDays(null)}>
                Clear cohort focus
              </button>
            </div>
          )}
        </Card>
      </div>

      <div className="org-grid org-grid--table">
        <Card title="Session Feedback">
          <div className="nia-list" style={{ marginBottom: 12 }}>
            <div className="nia-list-btn" style={{ cursor: "default" }}>
              <span>Average rating</span>
              <span className="nia-pill">{avgRating || "—"}</span>
            </div>
            <div className="nia-list-btn" style={{ cursor: "default" }}>
              <span>Responses</span>
              <span className="nia-pill">{ratingSessions.length}</span>
            </div>
          </div>
          {ratingSessions.length > 0 ? (
            <>
              <div className="nia-list">
                {recentFeedback.length ? recentFeedback.map((s) => (
                  <div key={String(s.id)} className="nia-list-btn" style={{ cursor: "default" }}>
                    <span>{displaySessionName(s, "User")}</span>
                    <span className="nia-pill">{s.rating?.score}</span>
                    <span className="nia-muted" style={{ marginLeft: 8 }}>{maskSessionText(s, s.rating?.feedback)}</span>
                  </div>
                )) : (
                  <div className="nia-muted">No written feedback yet.</div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-ghost" onClick={exportRatingsCsv}>
                  Export ratings CSV
                </button>
              </div>
            </>
          ) : (
            <div className="nia-muted">No ratings submitted yet.</div>
          )}
        </Card>
      </div>

      {cohortDays && (
        <div className="org-grid org-grid--table">
          <Card title={`Cohort Drill-down (${cohortDays} days)`}>
            <div className="nia-list" style={{ marginBottom: 12 }}>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Sessions</span>
                <span className="nia-pill">{cohortSessions.length}</span>
              </div>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Users</span>
                <span className="nia-pill">{cohortUsers}</span>
              </div>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Top block</span>
                <span className="nia-pill">{cohortTopBlocks[0]?.name || "—"}</span>
              </div>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Top game</span>
                <span className="nia-pill">{cohortTopGames[0]?.name || "—"}</span>
              </div>
            </div>
            <Table
              headers={["Date", "User", "Messages", "Block", "Game"]}
              rows={cohortSessions.slice(0, 6).map((s) => [
                formatDate(s.dateISO || s.createdAt),
                displaySessionName(s, "User"),
                s.msgs || 0,
                s.block || "-",
                s.game || "-",
              ])}
              caption="Most recent cohort sessions"
            />
            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const { safeName, stamp } = getExportMeta();
                  const csv = buildInsightsCsv(cohortSessions);
                  downloadCsv(csv, `nia-cohort-${safeName}-${stamp}-${cohortDays}d.csv`);
                }}
              >
                Export cohort CSV
              </button>
            </div>
          </Card>
        </div>
      )}

      <div className="org-grid org-grid--charts">
        <Card title="Top Blocks (week)">
          {topBlocks.length ? (
            <div className="org-chart">
              <ResponsiveContainer width="100%" height={260} minWidth={10} minHeight={10} debounce={120}>
                <BarChart data={topBlocks} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="name" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                  <YAxis stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    cursor={{ fill: "color-mix(in srgb, var(--ui-accent-2) 16%, transparent)" }}
                  />
                  <Bar dataKey="count" fill="var(--ui-accent)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="nia-muted">No block data is available for the current session set.</div>
          )}
        </Card>

        <Card title="Game Share">
          {gameShare.length ? (
            <div className="org-chart">
              <ResponsiveContainer width="100%" height={260} minWidth={10} minHeight={10} debounce={120}>
                <PieChart>
                  <Pie
                    data={gameShare}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    innerRadius={45}
                    paddingAngle={3}
                  >
                    {gameShare.map((entry, index) => (
                      <Cell key={`cell-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Legend wrapperStyle={{ color: "var(--ui-muted)", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="nia-muted">No game usage data is available for the current session set.</div>
          )}
        </Card>
      </div>

      {roleGameHighlights.length > 0 && (
        <div className="org-grid org-grid--table">
          <Card title="Role/Game Highlights">
            <div className="nia-list">
              {roleGameHighlights.map((row) => (
                <div key={row.role} className="nia-list-btn" style={{ cursor: "default" }}>
                  <span>{row.role}</span>
                  <span className="nia-pill">{row.game}</span>
                  <span className="nia-pill">{row.count} sessions</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="org-grid org-grid--table">
        <Card title="Sessions Trend">
          {hasSessionData ? (
            <div className="org-chart">
              <ResponsiveContainer width="100%" height={220} minWidth={10} minHeight={10} debounce={120}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                  <YAxis stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Line type="monotone" dataKey="sessions" stroke="var(--ui-accent)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="nia-muted">No dated sessions are available for trend analysis.</div>
          )}
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-ghost" onClick={exportTrendCsv}>
              Export Trend CSV
            </button>
          </div>
        </Card>
      </div>

      <div className="org-grid org-grid--charts">
        <Card title="Blocks by Role">
          <div className="org-chart">
            <ResponsiveContainer width="100%" height={260} minWidth={10} minHeight={10} debounce={120}>
              <BarChart data={blocksByRole} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="role" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <YAxis stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <Tooltip contentStyle={chartTooltipStyle} />
                {Object.keys(blocksByRole[0] || {}).filter((k) => k !== "role").map((key, idx) => (
                  <Bar key={key} dataKey={key} fill={pieColors[idx % pieColors.length]} stackId="blocks" />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Games by Role">
          <div className="org-chart">
            <ResponsiveContainer width="100%" height={260} minWidth={10} minHeight={10} debounce={120}>
              <BarChart data={gamesByRole} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="role" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <YAxis stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <Tooltip contentStyle={chartTooltipStyle} />
                {Object.keys(gamesByRole[0] || {}).filter((k) => k !== "role").map((key, idx) => (
                  <Bar key={key} dataKey={key} fill={pieColors[(idx + 2) % pieColors.length]} stackId="games" />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="org-grid org-grid--table">
        <Card title={recentSessionsTitle}>
          {tableRows.length ? (
            <Table
              headers={tableHeaders}
              rows={tableRows}
              onRowClick={(index) => setSelectedSession(recentSessions[index])}
            />
          ) : (
            <div className="nia-muted">
              {sessionsLoaded ? "No sessions matched the current org filters." : "Loading session data..."}
            </div>
          )}
        </Card>
      </div>

      <div className="org-grid org-grid--table">
        <Card title="Top Users">
          {topUserRows.length ? (
            <Table
              headers={["User", "Sessions", "Messages"]}
              rows={topUserRows}
              onRowClick={(index) => setSelectedUser(topUsers[index]?.user || "")}
            />
          ) : (
            <div className="nia-muted">No user activity is available for the current filters.</div>
          )}
          <div style={{ marginTop: 10 }}>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const { safeName, stamp } = getExportMeta();
                const csv = buildInsightsCsv(
                  topUsers.map((u) => ({
                    user: u.user,
                    dateISO: "",
                    msgs: u.messages,
                    preview: "",
                    block: "",
                    game: "",
                  }))
                );
                downloadCsv(csv, `nia-top-users-${safeName}-${stamp}.csv`);
              }}
            >
              Export Top Users CSV
            </button>
            {selectedUser && (
              <button className="btn btn-ghost" onClick={() => setSelectedUser("")}>
                Clear user filter
              </button>
            )}
          </div>
        </Card>
      </div>

      {selectedUser && (
        <div className="org-grid org-grid--table">
          <Card title={`User Drill-down: ${maskSessionText({ user: selectedUser }, selectedUser)}`}>
            <div className="nia-list" style={{ marginBottom: 12 }}>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Sessions</span>
                <span className="nia-pill">{userSessions.length}</span>
              </div>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Top block</span>
                <span className="nia-pill">{userTopBlocks[0]?.name || "—"}</span>
              </div>
              <div className="nia-list-btn" style={{ cursor: "default" }}>
                <span>Top game</span>
                <span className="nia-pill">{userTopGames[0]?.name || "—"}</span>
              </div>
            </div>
            <Table
              headers={["Date", "Messages", "Block", "Game"]}
              rows={userSessions.slice(0, 6).map((s) => [
                formatDate(s.dateISO || s.createdAt),
                s.msgs || 0,
                s.block || "-",
                s.game || "-",
              ])}
              caption="Most recent sessions"
            />
            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const { safeName, stamp } = getExportMeta();
                  const csv = buildInsightsCsv(userSessions);
                  downloadCsv(csv, `nia-user-${safeName}-${stamp}-${selectedUser.toLowerCase().replace(/\\s+/g, "-")}.csv`);
                }}
              >
                Export user sessions CSV
              </button>
            </div>
          </Card>
        </div>
      )}

      {selectedSession && (
        <div style={{ position: "fixed", inset: 0, background: "var(--color-overlay)", zIndex: 999 }}>
          <div style={{ maxWidth: 520, margin: "10% auto", background: "var(--ui-surface)", padding: 20, borderRadius: 12, border: "1px solid var(--ui-border)" }}>
            <h3 style={{ marginTop: 0 }}>Session Details</h3>
            <div className="nia-form" style={{ display: "grid", gap: 8 }}>
              <div><strong>User:</strong> {getSessionDisplayName(selectedSession, user?.role || "member")}</div>
              <div><strong>Email:</strong> {getSessionDisplayEmail(selectedSession, user?.role || "member") || "Hidden"}</div>
              <div><strong>Role:</strong> {selectedSession.role || "member"}</div>
              <div><strong>Date:</strong> {formatDate(selectedSession.dateISO || selectedSession.createdAt)}</div>
              <div><strong>Game:</strong> {selectedSession.game || "-"}</div>
              <div><strong>Block:</strong> {selectedSession.block || "-"}</div>
              <div><strong>Messages:</strong> {selectedSession.msgs || 0}</div>
              <div><strong>Preview:</strong> {maskSessionText(selectedSession, selectedSession.preview || "-")}</div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary" onClick={() => setSelectedSession(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .org-insights {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .org-insights__header h2 {
          margin: 0 0 6px;
          color: color-mix(in srgb, var(--ui-text) 90%, var(--color-primary) 10%);
          line-height: 1.12;
          letter-spacing: -0.02em;
        }

        .org-insights__header p {
          margin: 0;
          color: color-mix(in srgb, var(--ui-text) 42%, var(--ui-muted) 58%);
          line-height: 1.55;
        }

        .org-grid {
          display: grid;
          gap: 16px;
        }

        .org-grid--kpis {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .org-grid--charts {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .org-grid--table {
          grid-template-columns: 1fr;
        }

        .org-chart {
          height: 260px;
        }

        @media (max-width: 1100px) {
          .org-grid--kpis {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 720px) {
          .org-grid--kpis,
          .org-grid--charts {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
